import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, FolderKanban, AlertTriangle, CalendarClock, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CaseCard, StatCard, SectionTitle, EmptyState } from "@/components/CaseCard";
import { formatDate } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
      </div>
    );
  }

  const s = data.stats;
  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-patria-ink">Welcome back, {user.name.split(" ")[0]}</h1>
          <p className="text-patria-muted mt-1">Your team's active COMPASS cases and check-ins.</p>
        </div>
        <Button onClick={() => navigate("/cases/new")} data-testid="start-new-case" className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface">
          <Plus className="h-4 w-4 mr-2" /> Log feedback / start case
        </Button>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active cases" value={s.active} icon={FolderKanban} tone="green" testId="stat-active" />
        <StatCard label="Overdue check-ins" value={s.overdue} icon={AlertTriangle} tone={s.overdue ? "red" : "neutral"} testId="stat-overdue" />
        <StatCard label="At risk" value={s.at_risk} icon={Clock} tone={s.at_risk ? "amber" : "neutral"} testId="stat-atrisk" />
        <StatCard label="Closed" value={s.closed} icon={FolderKanban} tone="neutral" testId="stat-closed" />
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionTitle action={<Button variant="ghost" size="sm" className="text-patria-green" onClick={() => navigate("/cases")} data-testid="view-all-cases">View all</Button>}>
            My active cases
          </SectionTitle>
          {data.active_cases.length === 0 ? (
            <EmptyState title="No active cases" hint="Start by logging documented feedback for a team member." testId="empty-active-cases" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {data.active_cases.map((c) => <CaseCard key={c.id} c={c} />)}
            </div>
          )}
        </div>

        <div>
          <SectionTitle>Upcoming & overdue check-ins</SectionTitle>
          {data.upcoming_checkins.length === 0 ? (
            <EmptyState title="Nothing scheduled" hint="Check-ins appear here once a plan is active." testId="empty-checkins" />
          ) : (
            <div className="space-y-2">
              {data.upcoming_checkins.map((u, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/cases/${u.case_id}`)}
                  data-testid={`checkin-item-${i}`}
                  className="w-full text-left rounded-xl bg-patria-surface border border-patria-muted/15 px-4 py-3 hover:border-patria-green/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-patria-ink">{u.employee_name}</span>
                    <span className={`text-xs inline-flex items-center gap-1 ${u.overdue ? "text-patria-terracottaDeep font-medium" : "text-patria-muted"}`}>
                      {u.overdue ? <AlertTriangle className="h-3.5 w-3.5" /> : <CalendarClock className="h-3.5 w-3.5" />}
                      {formatDate(u.date)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
